import { DeliveryCard, DispatchCard } from 'src/entities/dispatch';
import { PlanningsLegRunOperationEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { RotationRow } from 'src/pages/ConvenienceDispatches/adapter';
import { coordKeyOf } from 'src/pages/ConvenienceDispatches/placeKeys';

import { TripSpan } from './types';

/**
 * 割当状態の正になるカードか。
 *
 * Why not LoadingCard も見る: placeLegRunInRow は同じ legRunId を積地・降地の両方へ
 * 足すが、行間移動は掴んだ降地カード 1 枚しか消さない。両方数えると移動元に
 * 積地カードの ID が残り、バーが消えない。runAutoDispatch の所在判定と同じ規則。
 */
export const isAssignedDeliveryCard = (card: DispatchCard): card is DeliveryCard =>
  card.type === 'delivery' && card.status !== 'no-delivery' && card.status !== 'fr-template';

/**
 * 実時刻で比べる。
 *
 * Why not 文字列比較: ISO はオフセットを含むので、'+09:00' と 'Z' が混在すると
 * 辞書順と実時刻順が一致しない。
 */
const minOf = (values: string[]): string | null =>
  (values.length === 0 ? null : values.reduce((a, b) => (Date.parse(a) <= Date.parse(b) ? a : b)));

const maxOf = (values: string[]): string | null =>
  (values.length === 0 ? null : values.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b)));

/** バー 1 本ぶん。1 回転の中を積地で分けた単位 */
export type TripSegment = {
  /** 積地の同一性キー。`placeKeys.coordKeyOf` と同じ規則 */
  loadingKey: string;
  legRunIds: number[];
  span: TripSpan;
};

type Times = { loadArrivals: string[]; unloadArrivals: string[]; unloadDepartures: string[] };

/**
 * 1 回転を**積地ごと**のバーに分ける。
 *
 * 積地 A から B・C へ配送する案件は 1 本、そこへ積地 D → E の案件が乗れば 2 本になる
 * （ユーザー指摘 2026-09-29）。サンプル（route-planner MultiDayBoard.tsx:455-497）も
 * order（積地 1 + 降地 N）ごとに 1 本のバーを作る。
 *
 * Why not 回転ごとに 1 本（旧実装）: 別の積地で積んだ荷物が 1 本の線に混ざり、
 * バーに出る積地名も先頭 1 件だけになるので「いつどこで積んだか」が読めない。
 *
 * 積地の同一判定を `coordKeyOf` にするのは、名前だけで見ると表記揺れで同じ拠点が
 * 2 本に割れるため。既存の地点キー（`placeKeys.ts`）と同じ規則に揃える。
 *
 * Why not `placeKeyOf`（カードの同一性キー）: あちらは「配送リストの地点表示」設定に
 * 従って名称も混ぜる。長距離のバーが表すのは「どこで積んだか」という場所そのもので、
 * 同じ場所での作業をまとめたい処理にあたる（`coordKeyOf` の Why と同じ）。
 */
export function resolveTripSpans(
  row: RotationRow,
  legRunById: ReadonlyMap<number, ShipmentOrderLegRunForPlanEntity>,
): TripSegment[] {
  const legRunIds = Array.from(new Set(
    row.cards.filter(isAssignedDeliveryCard).flatMap((card) => card.legRunIds ?? []),
  ));
  if (legRunIds.length === 0) return [];

  const opsByLegRunId = new Map<number, PlanningsLegRunOperationEntity[]>();
  for (const sourceCard of row.source?.cards ?? []) {
    for (const op of sourceCard.operations ?? []) {
      const list = opsByLegRunId.get(op.shipmentOrderLegRunId) ?? [];
      list.push(op);
      opsByLegRunId.set(op.shipmentOrderLegRunId, list);
    }
  }

  const groups = new Map<string, { legRunIds: number[] } & Times>();

  for (const id of legRunIds) {
    const ops = opsByLegRunId.get(id) ?? [];
    const legRun = legRunById.get(id);
    const loadOp = ops.find((op) => op.action === '積');
    const unloadOp = ops.find((op) => op.action === '降');

    const loadArrival = loadOp?.arrivalAt ?? legRun?.loading_start_at;
    const unloadArrival = unloadOp?.arrivalAt ?? legRun?.unloading_start_at;
    const unloadDeparture = unloadOp?.departureAt ?? legRun?.unloading_end_at;

    // legRun が引けなければ他と混ぜない（積地が分からないので単独のバーにする）
    const loadingKey = coordKeyOf('loading', legRun, `legRun:${id}`);
    const group = groups.get(loadingKey)
      ?? { legRunIds: [], loadArrivals: [], unloadArrivals: [], unloadDepartures: [] };
    group.legRunIds.push(id);
    if (loadArrival) group.loadArrivals.push(loadArrival);
    if (unloadArrival) group.unloadArrivals.push(unloadArrival);
    if (unloadDeparture) group.unloadDepartures.push(unloadDeparture);
    groups.set(loadingKey, group);
  }

  const segments: TripSegment[] = [];
  for (const [loadingKey, group] of groups) {
    const displayStartAt = minOf(group.loadArrivals);
    const displayEndAt = maxOf(group.unloadArrivals);
    // 積か降のどちらかの時刻が埋まらない積地はバーにできない（旧実装と同じ判定）
    if (!displayStartAt || !displayEndAt) continue;
    segments.push({
      loadingKey,
      legRunIds: group.legRunIds,
      span: {
        displayStartAt,
        displayEndAt,
        occupyStartAt: displayStartAt,
        occupyEndAt: maxOf(group.unloadDepartures) ?? displayEndAt,
      },
    });
  }

  // 早く積んだものから並べる（サンプル MultiDayBoard.tsx:466 も loadOffset 順）
  return segments.sort(
    (a, b) => Date.parse(a.span.displayStartAt) - Date.parse(b.span.displayStartAt),
  );
}
